import { FC, useEffect } from 'react';

import startCase from 'lodash/startCase';

import { useCreateMerchantSettingsMutation } from '@entities/merchant';
import { FormItemWithError } from '@shared/components/Form';
import Loading from '@shared/components/Loading';
import { useModal } from '@shared/components/Modal';

import {
  assmebleMerchantSettings,
  MERCHANT_SETTINGS_KEYS,
} from '../utils/assembleMerchantSettings';

interface IProps {
  parent_id: string;
  parent_name: string;
  refetch: () => void;
}

export const NewMerchantSettings: FC<IProps> = ({ parent_id, parent_name, refetch }) => {
  const { closeModal } = useModal();
  const [createMerchantSettings, { isLoading, isError, error }] =
    useCreateMerchantSettingsMutation();

  useEffect(() => {
    const submitDefaultSettings = async () => {
      const settings = assmebleMerchantSettings(new FormData());
      await createMerchantSettings({ id: parent_id, ...settings });
      refetch();
    };
    submitDefaultSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const settings = assmebleMerchantSettings(formData);
    try {
      const result = await createMerchantSettings({ id: parent_id, ...settings });
      if ('data' in result) {
        closeModal();
        refetch();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {isLoading && <Loading />}

      <h2>
        New merchant settings for <b>{parent_name}</b>
      </h2>
      <form onSubmit={handleSubmit} className='popup-form'>
        {MERCHANT_SETTINGS_KEYS.map(key => {
          switch (key) {
            case 'deviation':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={'Deposit Amount Threshold (%)'}
                  error={error}
                  isError={isError}
                >
                  <input type='number' name={key} step={1} min={0} max={100} defaultValue='0' />
                </FormItemWithError>
              );
            case 'threshold':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={'Min Deposit Amount'}
                  error={error}
                  isError={isError}
                >
                  <input type='number' name={key} step={1} min={0} defaultValue='0' />
                </FormItemWithError>
              );

            case 'denied_withdraw_with_negative_balance':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={startCase(key)}
                  error={error}
                  isError={isError}
                >
                  <select name={key}>
                    <option value='true'>True</option>
                    <option value='false'>False</option>
                  </select>
                </FormItemWithError>
              );

            case 'is_priority':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={startCase(key)}
                  error={error}
                  isError={isError}
                >
                  <select name={key} defaultValue='false'>
                    <option value='true'>True</option>
                    <option value='false'>False</option>
                  </select>
                </FormItemWithError>
              );

            case 'support_slip_upload':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={startCase(key)}
                  error={error}
                  isError={isError}
                >
                  <select name={key}>
                    <option value='true'>True</option>
                    <option value='false'>False</option>
                  </select>
                </FormItemWithError>
              );

            case 'metabase_analytics_dashboard_id':
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={startCase(key)}
                  error={error}
                  isError={isError}
                >
                  <input type='text' name={key} defaultValue='' />
                </FormItemWithError>
              );
            default:
              return (
                <FormItemWithError
                  key={key}
                  name={key}
                  label={startCase(key)}
                  error={error}
                  isError={isError}
                >
                  <select name={key}>
                    <option value='true'>Show</option>
                    <option value='false'>Hide</option>
                  </select>
                </FormItemWithError>
              );
          }
        })}

        <div className='form-submit'>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
          <button type='submit' disabled={isLoading}>
            Save
          </button>
        </div>
      </form>
    </>
  );
};
